"use client";

import { Box, BoxProps, Container, ContainerProps, Stack, StackProps } from "@mui/material";
import { CSSProperties, FC } from "react";
import SalifyComponent from "../salify-component/default";
import { SalifyLinkV2 } from "../ui/link/link";
import { toAssetImages } from "src/lib/utils";
import { PositionAbsouteType, positionAbsouteTypeValue } from "src/lib/utils/types";

type BannerBoxCss = BoxProps & {
  backgroundSize?: CSSProperties["backgroundSize"];
  backgroundRepeat?: CSSProperties["backgroundRepeat"];
  backgroundImage?: CSSProperties["backgroundImage"];
  backgroundPosition?: CSSProperties["backgroundPosition"];
};

interface SalifyBannerWrapperDefaultProps {
  imgSrc: any;
  link: any;
  content: {
    position: PositionAbsouteType;
    stackProps: StackProps;
    containerProps: ContainerProps;
    components: any[];
  };
  cssProps?: BannerBoxCss;
}

const SalifyBannerWrapperDefault: FC<SalifyBannerWrapperDefaultProps> = (props) => {
  const hasHeight = props.cssProps?.height || props.cssProps?.minHeight;

  const resolvedImg =
    typeof props.imgSrc === "string" && props.imgSrc.trim() !== ""
      ? toAssetImages(props.imgSrc)
      : "";

  const {
    sx: boxCssPropsSx,
    backgroundSize: boxBackgroundSize,
    backgroundRepeat: boxBackgroundRepeat,
    backgroundImage: boxBackgroundImage,
    backgroundPosition: boxBackgroundPosition,
    ...boxCssRest
  } = (props.cssProps ?? {}) as BannerBoxCss;

  return (
    <SalifyLinkV2 href={props.link}>
      <Box
        position="relative"
        height={hasHeight ? "100%" : undefined}
        sx={{
          ...(hasHeight && resolvedImg
            ? {
                backgroundImage: `url(${resolvedImg})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
              }
            : {}),
          ":hover img": {
            transform: "scale(1.05)",
          },
          ...(boxBackgroundSize != null && { backgroundSize: boxBackgroundSize }),
          ...(boxBackgroundRepeat != null && { backgroundRepeat: boxBackgroundRepeat }),
          ...(boxBackgroundImage != null && { backgroundImage: boxBackgroundImage }),
          ...(boxBackgroundPosition != null && { backgroundPosition: boxBackgroundPosition }),
          ...boxCssPropsSx,
        }}
        {...boxCssRest}
      >
        {props.content?.components && !!props.content?.components.length && (
          <Stack
            position="absolute"
            {...props.content?.stackProps}
            sx={{
              inset: 0,
              zIndex: 1,
              ...props.content?.stackProps?.sx,
            }}
          >
            <Container
              maxWidth={false}
              disableGutters
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                ...positionAbsouteTypeValue[
                  (props.content?.position as PositionAbsouteType) || "top-left"
                ],
              }}
            >
              <Container
                disableGutters
                {...props.content?.containerProps}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  ...positionAbsouteTypeValue[
                    (props.content?.position as PositionAbsouteType) || "top-left"
                  ],
                  ...props.content?.containerProps?.sx,
                }}
              >
                <SalifyComponent components={props.content.components} />
              </Container>
            </Container>
          </Stack>
        )}

        {!hasHeight && resolvedImg && (
          <Box component="div" overflow="hidden">
            <Box
              component="img"
              src={resolvedImg}
              alt=""
              loading="lazy"
              sx={{
                zIndex: 0,
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "scale(1.1)",
                },
              }}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "inherit",
                objectFit: "cover",
              }}
            />
          </Box>
        )}
      </Box>
    </SalifyLinkV2>
  );
};

SalifyBannerWrapperDefault.displayName = "SalifyBannerWrapperDefault";

export default SalifyBannerWrapperDefault;
